import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { team } from '@/mocks/site';

const standards = [
  { t: 'Fear-Free trained', d: 'Every clinician on our team has completed Fear-Free certification.' },
  { t: 'AAHA-aligned protocols', d: 'We follow current American Animal Hospital Association guidelines.' },
  { t: 'Modern anesthesia', d: 'Individualized plans, dedicated monitoring, and warm recovery for every patient.' },
  { t: 'Clear communication', d: 'Written plans, honest estimates, and follow-up we actually make.' },
];

export default function About() {
  return (
    <Layout>
      {/* COMPACT SUBPAGE INTRO */}
      <section className="relative overflow-hidden bg-white">
        <span className="deco-float-sm absolute pointer-events-none hidden md:block" style={{ width: 360, height: 360, top: -160, right: -120, background: '#F2D16B', opacity: 0.45, borderRadius: '58% 42% 63% 37% / 47% 55% 45% 53%' }} aria-hidden />
        <div className="relative max-w-container mx-auto px-5 md:px-12 pt-16 md:pt-20 pb-10 md:pb-14">
          <div className="text-[13px] font-semibold uppercase tracking-[0.18em] text-cocoa">Our Approach</div>
          <h1 className="font-display mt-4 font-bold leading-[1.0] text-cocoa text-[38px] md:text-[52px] max-w-[820px]">
            A neighborhood clinic built around the relationship.
          </h1>
          <p className="mt-5 text-[16px] md:text-[17px] leading-relaxed text-ink/75 max-w-[620px]">
            We opened Alder because we wanted a different kind of vet visit — one that respects your pet's pace, and yours.
          </p>
        </div>
      </section>

      {/* PHILOSOPHY — warm white with soft blob */}
      <section className="relative overflow-hidden py-24 md:py-32" style={{ background: '#FFF9F2' }}>
        <span className="deco-float-lg absolute pointer-events-none hidden md:block" style={{ width: 560, height: 560, top: -220, right: -180, background: '#FBEBD6', borderRadius: '58% 42% 63% 37% / 47% 55% 45% 53%' }} aria-hidden />
        <span className="deco-float-sm-alt absolute pointer-events-none hidden md:block" style={{ width: 420, height: 420, bottom: -180, left: -160, background: '#FFF3E4', borderRadius: '52% 48% 39% 61% / 58% 44% 56% 42%' }} aria-hidden />

        <div className="relative max-w-container mx-auto px-5 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-cocoa">Philosophy</div>
            <h2 className="mt-4 font-display font-bold text-[40px] md:text-[56px] leading-[1.0] text-cocoa">
              Care that reads the room.
            </h2>
            <p className="mt-6 text-[17px] text-ink/75 leading-relaxed max-w-[440px]">
              We designed the visit around your pet's comfort — because good medicine starts with a calm patient and a listening clinician.
            </p>
          </div>
          <div className="lg:col-span-7 space-y-6">
            <div className="rounded-[32px] border border-cocoa/15 bg-white p-8 md:p-10">
              <h3 className="font-display font-semibold text-[22px] md:text-[24px] text-cocoa">A design problem, not a pet problem</h3>
              <p className="mt-3 text-[16px] text-ink/85 leading-relaxed">
                Most pets don't love the vet. We think that's a design problem — not a pet problem. So we redesigned the visit: quieter rooms, more time, treats at reception, and clinicians who kneel down to say hello before anything else.
              </p>
            </div>
            <div className="rounded-[32px] border border-cocoa/15 bg-white p-8 md:p-10">
              <h3 className="font-display font-semibold text-[22px] md:text-[24px] text-cocoa">Observe first, treat second</h3>
              <p className="mt-3 text-[16px] text-ink/85 leading-relaxed">
                Before we reach for a stethoscope, we watch how your pet moves, breathes, and settles. Then we listen to you. You know your pet better than we ever will.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section id="team" className="py-24 md:py-32 bg-white">
        <div className="max-w-container mx-auto px-5 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-text">
              <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-cocoa">Doctors & Team</div>
              <h2 className="mt-3 font-display font-bold text-[40px] md:text-[56px] leading-[1.02] text-cocoa">
                Meet the people caring for your pet.
              </h2>
            </div>
          </div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {team.map((m) => (
              <div key={m.name}>
                <div className="relative w-full aspect-[4/5] overflow-hidden rounded-[36px] bg-line border border-line">
                  <img src={m.img} alt={m.name} decoding="async" className="absolute inset-0 w-full h-full object-cover object-top" />
                </div>
                <div className="mt-5">
                  <h3 className="font-display font-semibold text-[24px] text-cocoa">{m.name}</h3>
                  <p className="mt-1 text-[14px] text-muted">{m.credential}</p>
                  <p className="mt-2 text-[15px] text-ink/80">{m.specialty}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLINIC ENVIRONMENT */}
      <section className="py-24 md:py-32" style={{ background: '#FFF9F2' }}>
        <div className="max-w-container mx-auto px-5 md:px-12">
          <div className="max-w-text">
            <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-cocoa">Clinic Environment</div>
            <h2 className="mt-3 font-display font-bold text-[40px] md:text-[56px] leading-[1.02] text-cocoa">A calmer place to be a pet.</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="md:col-span-2 h-[420px] md:h-[540px] overflow-hidden shape-1 bg-line">
              <img src="https://readdy.ai/api/search-image?query=Editorial%20photograph%20of%20a%20warm%20bright%20modern%20veterinary%20exam%20room%20with%20soft%20off%20white%20walls%2C%20wood%20details%2C%20a%20soft%20blanket%20on%20the%20exam%20table%2C%20natural%20daylight%20from%20windows%2C%20calm%20inviting%20clinical%20setting%2C%20real%20architectural%20photography&width=960&height=600&seq=env-1&orientation=landscape" alt="Exam room" className="w-full h-full object-cover object-top" />
            </div>
            <div className="h-[420px] md:h-[540px] overflow-hidden shape-3 bg-line">
              <img src="https://readdy.ai/api/search-image?query=Editorial%20photograph%20of%20a%20warm%20quiet%20cat%20friendly%20waiting%20nook%20inside%20a%20modern%20veterinary%20clinic%20with%20soft%20off%20white%20walls%2C%20plants%2C%20a%20small%20wooden%20bench%2C%20natural%20daylight%2C%20calm%20inviting%20atmosphere&width=540&height=720&seq=env-2&orientation=portrait" alt="Waiting area" className="w-full h-full object-cover object-top" />
            </div>
          </div>
        </div>
      </section>

      {/* CARE STANDARDS — bubble modules */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-container mx-auto px-5 md:px-12">
          <div className="max-w-text">
            <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-cocoa">Care Standards</div>
            <h2 className="mt-3 font-display font-bold text-[40px] md:text-[56px] leading-[1.02] text-cocoa">Modern medicine, quietly delivered.</h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {standards.map((s, i) => (
              <div key={s.t} className="rounded-[32px] border border-cocoa/15 bg-[#FFF9F2] p-8 md:p-10 flex gap-6">
                <div className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-display font-bold text-cocoa text-[18px]" style={{ background: '#F2D16B' }}>
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div>
                  <h3 className="font-display font-semibold text-[22px] text-cocoa">{s.t}</h3>
                  <p className="mt-2 text-[15px] text-ink/80 leading-relaxed">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA — yellow closer */}
      <section className="relative overflow-hidden py-24 md:py-28" style={{ background: '#F2D16B' }}>
        <span className="deco-float-lg-alt absolute pointer-events-none hidden md:block" style={{ width: 520, height: 520, top: -200, left: -160, background: '#F8E4A8', borderRadius: '58% 42% 63% 37% / 47% 55% 45% 53%' }} aria-hidden />
        <span className="deco-float-sm absolute pointer-events-none hidden md:block" style={{ width: 420, height: 420, bottom: -180, right: -140, background: '#EFC148', borderRadius: '52% 48% 39% 61% / 58% 44% 56% 42%', opacity: 0.55 }} aria-hidden />
        <div className="relative max-w-container mx-auto px-5 md:px-12 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <h2 className="font-display font-bold text-[36px] md:text-[52px] leading-[1.02] text-cocoa max-w-[640px]">
            Come meet the team.
          </h2>
          <Link to="/book" className="btn-motion group inline-flex items-center gap-2 h-[54px] px-8 rounded-full bg-cocoa hover:bg-careDark text-white text-[15px] font-semibold cursor-pointer whitespace-nowrap">
            Book an Appointment <i className="link-arrow-icon ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>
    </Layout>
  );
}